import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  BusinessOutlined as SupplierPickerIcon,
  Close as CloseIcon,
  ContactPhoneOutlined as ContactIcon,
  Inventory2Outlined as MaterialIcon,
  LocationOnOutlined as LocationIcon,
  Map as StreetsIcon,
  MapOutlined as MapIcon,
  OpenInNewOutlined as OpenIcon,
  PhoneOutlined as PhoneIcon,
  SatelliteAltOutlined as SatelliteIcon,
  SearchOutlined as SearchIcon,
} from '@mui/icons-material';
import type { FeatureCollection, Point } from 'geojson';
import type { GeoJSONSource } from 'maplibre-gl';
import Map, {
  Layer,
  NavigationControl,
  Source,
  type MapLayerMouseEvent,
  type MapRef,
} from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { APP_SHAPE } from '../../constants/shape';
import { mapStyleFor, type MapMode } from '../../data/mapStyles';
import type { SupplierCollectionPoint } from '../../types/supplier.type';
import LabelChips from '../common/LabelChips';

const RIYADH_VIEW = { longitude: 46.6753, latitude: 24.7136, zoom: 11 };
const POINT_COLOR = '#F4841A';
const SELECTED_POINT_COLOR = '#C4640D';

export interface CollectionPointsMapFilters {
  search: string;
  supplierId: string;
  materialId: string;
}

interface CollectionPointsMapProps {
  points: SupplierCollectionPoint[];
  loading?: boolean;
  materialLabels: Map<string, string>;
  supplierOptions: { id: string; name: string }[];
  supplierNameById: Map<string, string>;
  filters: CollectionPointsMapFilters;
  onFiltersChange: (next: Partial<CollectionPointsMapFilters>) => void;
  onOpenSupplier?: (supplierId: string) => void;
  height?: number | string;
}

function Detail({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr)', gap: 1.5 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex', '& svg': { fontSize: 20 } }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{children}</Typography>
      </Box>
    </Box>
  );
}

export default function CollectionPointsMap({
  points,
  loading,
  materialLabels,
  supplierOptions,
  supplierNameById,
  filters,
  onFiltersChange,
  onOpenSupplier,
  height = 'calc(100vh - 260px)',
}: CollectionPointsMapProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [mapMode, setMapMode] = useState<MapMode>('streets');
  const [mapRef, setMapRef] = useState<MapRef | null>(null);
  const [selectedPointId, setSelectedPointId] = useState<string | null>(null);

  const locatedPoints = useMemo(
    () => points.filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude)),
    [points],
  );
  const selectedPoint = locatedPoints.find((point) => point.id === selectedPointId) ?? null;

  const featureCollection = useMemo((): FeatureCollection<Point> => ({
    type: 'FeatureCollection',
    features: locatedPoints.map((point) => ({
      type: 'Feature',
      properties: { id: point.id },
      geometry: { type: 'Point', coordinates: [point.longitude, point.latitude] },
    })),
  }), [locatedPoints]);

  const onClick = useCallback(
    (event: MapLayerMouseEvent) => {
      const feats = event.target.queryRenderedFeatures(event.point, { layers: ['cp-clusters', 'cp-unclustered'] });
      if (!feats.length) {
        setSelectedPointId(null);
        return;
      }
      const feature = feats[0];
      if (feature.layer?.id === 'cp-clusters') {
        const clusterId = feature.properties?.cluster_id;
        const source = event.target.getSource('collection-points') as GeoJSONSource | undefined;
        if (source && clusterId != null) {
          source.getClusterExpansionZoom(clusterId).then((zoom) => {
            const [lng, lat] = (feature.geometry as Point).coordinates as [number, number];
            mapRef?.flyTo({ center: [lng, lat], zoom, duration: 500 });
          }).catch(() => {});
        }
        return;
      }
      const pointId = String(feature.properties?.id ?? '');
      const point = locatedPoints.find((p) => p.id === pointId);
      if (point) {
        setSelectedPointId(point.id);
        mapRef?.flyTo({ center: [point.longitude, point.latitude], zoom: Math.max(mapRef.getZoom(), 13), duration: 500 });
      }
    },
    [locatedPoints, mapRef],
  );

  const onMouseMove = useCallback((event: MapLayerMouseEvent) => {
    const feats = event.target.queryRenderedFeatures(event.point, { layers: ['cp-clusters', 'cp-unclustered'] });
    event.target.getCanvas().style.cursor = feats.length ? 'pointer' : '';
  }, []);

  return (
    <Box sx={{ position: 'relative', height, minHeight: 420, borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden' }}>
      <Map
        ref={setMapRef}
        initialViewState={RIYADH_VIEW}
        minZoom={3}
        maxZoom={18}
        mapStyle={mapStyleFor(mapMode)}
        style={{ width: '100%', height: '100%' }}
        attributionControl={{ compact: true }}
        onClick={onClick}
        onMouseMove={onMouseMove}
        interactiveLayerIds={['cp-clusters', 'cp-unclustered']}
      >
        <NavigationControl position="top-right" showCompass={false} />

        <Source
          id="collection-points"
          type="geojson"
          data={featureCollection}
          cluster
          clusterMaxZoom={14}
          clusterRadius={50}
        >
          <Layer
            id="cp-clusters"
            type="circle"
            filter={['has', 'point_count']}
            paint={{
              'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 50, 26],
              'circle-color': POINT_COLOR,
              'circle-opacity': 0.85,
              'circle-stroke-width': 2,
              'circle-stroke-color': theme.palette.background.paper,
            }}
          />
          <Layer
            id="cp-cluster-count"
            type="symbol"
            filter={['has', 'point_count']}
            layout={{
              'text-field': '{point_count_abbreviated}',
              'text-size': 12,
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            }}
            paint={{ 'text-color': '#ffffff' }}
          />
          <Layer
            id="cp-unclustered"
            type="circle"
            filter={['!', ['has', 'point_count']]}
            paint={{
              'circle-radius': ['case', ['==', ['get', 'id'], selectedPointId ?? ''], 10, 7],
              'circle-color': ['case', ['==', ['get', 'id'], selectedPointId ?? ''], SELECTED_POINT_COLOR, POINT_COLOR],
              'circle-stroke-width': 2,
              'circle-stroke-color': theme.palette.background.paper,
            }}
          />
        </Source>
      </Map>

      {/* HUD: point count, filters, basemap toggle, and legend float over the map as glass panels. */}
      <Chip
        icon={<LocationIcon />}
        label={loading ? t('suppliers.collectionPoints.map.loading') : t('suppliers.collectionPoints.map.pointCount', { count: locatedPoints.length })}
        sx={{
          position: 'absolute',
          top: 12,
          insetInlineStart: 12,
          zIndex: 5,
          bgcolor: alpha(theme.palette.background.paper, 0.94),
          borderRadius: `${APP_SHAPE.radiusSm}px`,
        }}
      />

      <ToggleButtonGroup
        size="small"
        value={mapMode}
        exclusive
        onChange={(_, next: MapMode | null) => next && setMapMode(next)}
        sx={{
          position: 'absolute',
          top: 12,
          insetInlineEnd: 52,
          zIndex: 5,
          bgcolor: alpha(theme.palette.background.paper, 0.94),
          borderRadius: `${APP_SHAPE.radiusSm}px`,
        }}
      >
        <ToggleButton value="streets" aria-label={t('suppliers.collectionPoints.map.streets')}><StreetsIcon fontSize="small" /></ToggleButton>
        <ToggleButton value="satellite" aria-label={t('suppliers.collectionPoints.map.satellite')}><SatelliteIcon fontSize="small" /></ToggleButton>
      </ToggleButtonGroup>

      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: 60,
          insetInlineStart: 12,
          zIndex: 5,
          width: { xs: 'calc(100% - 24px)', sm: 300 },
          maxWidth: 'calc(100% - 24px)',
          p: 2,
          borderRadius: `${APP_SHAPE.radiusLg}px`,
          bgcolor: alpha(theme.palette.background.paper, 0.96),
        }}
      >
        <Stack spacing={1.5}>
          <TextField
            size="small"
            placeholder={t('suppliers.collectionPoints.list.searchPlaceholder')}
            value={filters.search}
            onChange={(event) => onFiltersChange({ search: event.target.value })}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" color="action" sx={{ mr: 1 }} /> }}
          />
          <Autocomplete
            size="small"
            options={supplierOptions}
            value={supplierOptions.find((option) => option.id === filters.supplierId) ?? null}
            onChange={(_, value) => onFiltersChange({ supplierId: value?.id ?? '' })}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} placeholder={t('suppliers.collectionPoints.list.allSuppliers')} InputProps={{ ...params.InputProps, startAdornment: <SupplierPickerIcon fontSize="small" color="action" sx={{ mr: 0.5 }} /> }} />
            )}
          />
          <Autocomplete
            size="small"
            options={Array.from(materialLabels.entries()).map(([value, label]) => ({ value, label }))}
            value={filters.materialId ? { value: filters.materialId, label: materialLabels.get(filters.materialId) ?? filters.materialId } : null}
            onChange={(_, value) => onFiltersChange({ materialId: value?.value ?? '' })}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => (
              <TextField {...params} placeholder={t('suppliers.collectionPoints.list.allWasteTypes')} InputProps={{ ...params.InputProps, startAdornment: <MaterialIcon fontSize="small" color="action" sx={{ mr: 0.5 }} /> }} />
            )}
          />
        </Stack>
      </Paper>

      <Box
        sx={{
          position: 'absolute',
          bottom: 12,
          insetInlineStart: 12,
          zIndex: 5,
          px: 1.5,
          py: 1,
          borderRadius: `${APP_SHAPE.radiusSm}px`,
          bgcolor: alpha(theme.palette.background.paper, 0.94),
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <MapIcon fontSize="small" sx={{ color: POINT_COLOR }} />
        <Typography variant="caption">{t('suppliers.collectionPoints.map.legend')}</Typography>
      </Box>

      {loading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4, bgcolor: alpha(theme.palette.background.default, 0.4) }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {selectedPoint && (
        <Paper
          elevation={3}
          component="section"
          aria-label={t('suppliers.collectionPoints.detail.ariaLabel', { name: selectedPoint.name })}
          sx={{
            position: 'absolute',
            top: 12,
            insetInlineEnd: 12,
            bottom: 12,
            zIndex: 6,
            width: { xs: 'calc(100% - 24px)', sm: 340 },
            maxWidth: 'calc(100% - 24px)',
            overflow: 'auto',
            borderRadius: `${APP_SHAPE.radiusLg}px`,
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6">{selectedPoint.name}</Typography>
              {supplierNameById.get(selectedPoint.supplier_id) && (
                <Typography variant="caption" color="text.secondary">{supplierNameById.get(selectedPoint.supplier_id)}</Typography>
              )}
            </Box>
            <Tooltip title={t('suppliers.collectionPoints.detail.close')}>
              <IconButton aria-label={t('suppliers.collectionPoints.detail.close')} onClick={() => setSelectedPointId(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider />
          <Stack spacing={2.5} sx={{ p: 2.5 }}>
            <Detail icon={<LocationIcon />} label={t('suppliers.collectionPoints.detail.address')}>
              {selectedPoint.address || `${selectedPoint.latitude}, ${selectedPoint.longitude}`}
            </Detail>
            {selectedPoint.contact_name && <Detail icon={<ContactIcon />} label={t('suppliers.collectionPoints.detail.contact')}>{selectedPoint.contact_name}</Detail>}
            {selectedPoint.contact_phone && <Detail icon={<PhoneIcon />} label={t('suppliers.collectionPoints.detail.phone')}>{selectedPoint.contact_phone}</Detail>}
            {(selectedPoint.labels?.length ?? 0) > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  {t('suppliers.collectionPoints.table.labels')}
                </Typography>
                <LabelChips labels={selectedPoint.labels} />
              </Box>
            )}
            {(selectedPoint.material_ids?.length ?? 0) > 0 && (
              <Box>
                <Detail icon={<MaterialIcon />} label={t('suppliers.collectionPoints.detail.wasteTypes')}>{''}</Detail>
                <Stack direction="row" gap={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  {selectedPoint.material_ids.map((id) => (
                    <Chip key={id} size="small" label={materialLabels.get(id) ?? id} sx={{ borderRadius: `${APP_SHAPE.radiusSm}px` }} />
                  ))}
                </Stack>
              </Box>
            )}
            {onOpenSupplier && (
              <IconButton
                aria-label={t('suppliers.collectionPoints.detail.openSupplier')}
                size="small"
                onClick={() => onOpenSupplier(selectedPoint.supplier_id)}
                sx={{ justifySelf: 'start', width: 'fit-content', gap: 1, borderRadius: `${APP_SHAPE.radiusSm}px`, px: 1.5 }}
              >
                <OpenIcon fontSize="small" />
                <Typography variant="button">{t('suppliers.collectionPoints.detail.openSupplier')}</Typography>
              </IconButton>
            )}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
